import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Button, Grid } from "@mui/material";
import NoteBox from "../../components/noteBox/NoteBox";
import { useMisdeliveryInvestigationForm } from "../../@hooks/useMisdeliveryInvestigationForm";
import FormikRadioGroupField from "../../components/shared/form/FormikRadioGroupField";
import { FormikLabeledTextField } from "../../components/shared/form/FormikLabeledTextField";
import LabeledTextField from "../../components/shared/form/LabeledTextField";
import { SaveOrCompleteMSDInvestigation } from "../../API/saveOrCompleteMSDInvestigation";
import { checkPro } from "../../API/checkPro";
import { PopoutNotification } from "../../AppState/popoutNotification";
import NOTIFICATION_TYPE from "../../AppState/NotificationType";
import { useDispatch, useSelector } from "react-redux";
import { EFormAction, ErrorMessages, M_GRID_PROPS, S_GRID_PROPS, XL_GRID_PROPS } from "../../constants";
import Loader from "../../components/loader/loader";
import { IMisdeliveryInvestigation, MisDeliveryStatus } from "../../models";
import { REQUIRED_MSG } from "../../schema/formik";
import { hasCompleteMisdeliveryPermissionSelector } from "../../AppState/selectors/userPermissionsSelectors";
import { useCanViewByOwnershipPermission } from "../../@hooks/useCanViewByOwnershipPermission";
import { validateMisdeliveryBolPro } from "../../API/validateMisdeliveryBolPro";
import { compareObjects } from "../../helpers";
import { isFormValueChanged } from "../../AppState/isFormValueChanged";

import Location from "./../overageEntry/Location";
import { useNavigate } from "react-router-dom";
interface MisdeliveryInvestigationProps {
  osdNumber: number;
  investigation: IMisdeliveryInvestigation | null | undefined;
  investigationStatus?: MisDeliveryStatus;
  isExpanded?: boolean;
  entryBy?: string;
  assignedTo?: string;
  onRefresh?: () => void | Promise<void>;
  misdeliveredBolPro?: string | number;
  isCleared?: boolean;
}

const MisdeliveryInvestigation: React.FC<MisdeliveryInvestigationProps> = ({
  osdNumber,
  investigation,
  investigationStatus,
  isExpanded,
  entryBy,
  assignedTo,
  onRefresh,
  misdeliveredBolPro,
  isCleared
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { formik } = useMisdeliveryInvestigationForm(
    investigation ?? undefined,
  );
  const { values } = formik;
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const lastCreatedValuesRef = useRef<Record<string, any> | null>(null);
  const hasCompleteMisdeliveryPermission = useSelector(
    hasCompleteMisdeliveryPermissionSelector,
  );

  // Reset dependent fields when isFreightLabeled changes
  const prevIsFreightLabeledRef = useRef<string | undefined>(values.isFreightLabeled);
  useEffect(() => {
    // Skip clearing on initial mount
    if (prevIsFreightLabeledRef.current !== undefined && prevIsFreightLabeledRef.current !== values.isFreightLabeled) {
      // Clear the dependent answer so stale values don't remain when question visibility changes
      formik.setFieldValue("isFreightLabeledShowingMsdAddress", "");
    }
    prevIsFreightLabeledRef.current = values.isFreightLabeled;
  }, [values.isFreightLabeled, formik]);

  const canViewCompleteButton = useCanViewByOwnershipPermission({
    enabledFor: hasCompleteMisdeliveryPermission,
    entryBy,
    assignedTo,
  });

  const bolGenerated = investigationStatus === MisDeliveryStatus.bolGenerated

  const INVALID_PRO_MSG = "Invalid OD400#";

  const od400ProValidationSeqRef = useRef(0);
  const od400ProValidationCacheRef = useRef<{
    value: string;
    isValid: boolean;
  } | null>(null);

  const hasAttemptedCompleteRef = useRef(false);

  const handleOverageEntryClick = useCallback(async () => {
    const bolPro = (misdeliveredBolPro ?? "").toString().trim();

    if (!bolPro) {
      dispatch(
        PopoutNotification(
          NOTIFICATION_TYPE.ERROR,
          "BOL PRO is required to create an overage entry",
          Date.now().toString(),
        ),
      );
      return;
    }

    setLoading(true);
    try {
      const result = await validateMisdeliveryBolPro(bolPro);
      if (result?.success) {
        navigate(`/overage/entry?msdPro=${encodeURIComponent(bolPro)}`);
      } else {
        dispatch(
          PopoutNotification(
            NOTIFICATION_TYPE.ERROR,
            "Unable to validate BOL PRO",
            Date.now().toString(),
          ),
        );
      }
    } catch (error: unknown) {
      dispatch(
        PopoutNotification(
          NOTIFICATION_TYPE.ERROR,
          typeof error === "string" ? error : "Unable to validate BOL PRO",
          Date.now().toString(),
        ),
      );
    } finally {
      setLoading(false);
    }
  }, [dispatch, misdeliveredBolPro, navigate]);

  const isOd400Applicable = (nextValues: any) =>
    nextValues.isFreightInOsdBay === "N" &&
    nextValues.didFreightMoveOnOd400 === "Y";

  const applyOd400CachedErrorIfNeeded = (nextValues: any) => {
    if (!isOd400Applicable(nextValues)) return;

    const current = (nextValues.od400ProNumber ?? "").toString().trim();
    if (!current) return;

    if (!current.startsWith("555")) {
      formik.setFieldError("od400ProNumber", INVALID_PRO_MSG);
      return;
    }

    const cached = od400ProValidationCacheRef.current;
    if (cached?.value === current && cached?.isValid === false) {
      formik.setFieldError("od400ProNumber", INVALID_PRO_MSG);
    }
  };

  const validateOd400ProNumber = useCallback(
    async (rawValue: string): Promise<boolean> => {
      const value = rawValue.trim();
      if (!value) return true;

      // Rule 1: must start with 555
      if (!value.startsWith("555")) {
        od400ProValidationCacheRef.current = { value, isValid: false };
        formik.setFieldTouched("od400ProNumber", true, false);
        formik.setFieldError("od400ProNumber", INVALID_PRO_MSG);
        return false;
      }

      const seq = ++od400ProValidationSeqRef.current;
      try {
        const response = await checkPro(value);
        // Ignore stale responses if the user changed the field quickly.
        if (seq !== od400ProValidationSeqRef.current) return true;

        const isBlankObject =
          response &&
          typeof response === "object" &&
          Object.keys(response as any).length === 0;

        const isValid = Boolean(response && !isBlankObject);
        od400ProValidationCacheRef.current = { value, isValid };
        formik.setFieldTouched("od400ProNumber", true, false);

        if (isValid) {
          if ((formik.errors as any)?.od400ProNumber === INVALID_PRO_MSG) {
            formik.setFieldError("od400ProNumber", undefined);
          }
        } else {
          formik.setFieldError("od400ProNumber", INVALID_PRO_MSG);
        }

        return isValid;
      } catch {
        if (seq !== od400ProValidationSeqRef.current) return true;
        od400ProValidationCacheRef.current = { value, isValid: false };
        formik.setFieldTouched("od400ProNumber", true, false);
        formik.setFieldError("od400ProNumber", INVALID_PRO_MSG);
        return false;
      }
    },
    [formik],
  );

  const prevValuesRef = useRef(values);

  // Helper: Reset field value, touched state, and error
  const resetField = useCallback(
    (fieldName: string, value: unknown, currentValues: any) => {
      if (currentValues[fieldName] !== value) {
        formik.setFieldValue(fieldName, value, false);
      }
      formik.setFieldTouched(fieldName, false, false);
      formik.setFieldError(fieldName, undefined);
    },
    [formik],
  );

  // Helper: Handle pickup flow field changes
  const handlePickupFlowChanges = useCallback(
    (prev: any, next: any) => {
      if (prev.wasPickUpCompleted !== next.wasPickUpCompleted) {
        if (next.wasPickUpCompleted !== "N") {
          resetField("wasPickUpRescheduled", "", next);
          resetField("pickupNumber", undefined, next);
          resetField("reasonUnableToPickUp", "", next);
        }
      }

      if (prev.wasPickUpRescheduled !== next.wasPickUpRescheduled) {
        if (next.wasPickUpRescheduled === "Y") {
          resetField("reasonUnableToPickUp", "", next);
        } else if (next.wasPickUpRescheduled === "N") {
          resetField("pickupNumber", undefined, next);
        } else {
          resetField("pickupNumber", undefined, next);
          resetField("reasonUnableToPickUp", "", next);
        }
      }
    },
    [resetField],
  );

  // Helper: Handle OSD bay and OD400 flow changes
  const handleOsdBayFlowChanges = useCallback(
    (prev: any, next: any) => {
      if (prev.isFreightInOsdBay !== next.isFreightInOsdBay) {
        if (next.isFreightInOsdBay !== "N") {
          resetField("didFreightMoveOnOd400", "", next);
          resetField("od400ProNumber", undefined, next);
        }
      }

      if (prev.didFreightMoveOnOd400 !== next.didFreightMoveOnOd400) {
        if (next.didFreightMoveOnOd400 !== "Y") {
          resetField("od400ProNumber", undefined, next);
        }
      }
    },
    [resetField],
  );

  // Helper: Handle BOL and address field changes
  const handleBolAndAddressChanges = useCallback(
    (prev: any, next: any) => {

      if (
        prev.isFreightLabeledShowingMsdAddress !==
        next.isFreightLabeledShowingMsdAddress
      ) {
        if (next.isFreightLabeledShowingMsdAddress !== "N") {
          resetField("misdeliveryAddress1", "", next);
          resetField("misdeliveryAddress2", "", next);
          resetField("misdeliveryCity", "", next);
          resetField("misdeliveryState", "", next);
          resetField("misdeliveryZip", "", next);
        }
      }
    },
    [resetField],
  );

  useEffect(() => {
    const prev = prevValuesRef.current;
    const next = values;

    handlePickupFlowChanges(prev, next);
    handleOsdBayFlowChanges(prev, next);
    applyOd400CachedErrorIfNeeded(next);
    handleBolAndAddressChanges(prev, next);

    prevValuesRef.current = next;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values]);

  const touchAllFields = useCallback(() => {
    const allTouched = Object.keys(formik.values).reduce(
      (acc, key) => {
        acc[key] = true;
        return acc;
      },
      {} as Record<string, boolean>,
    );

    formik.setTouched(allTouched, false);
    return allTouched;
  }, [formik]);

  const YES_NO_OPTIONS = [
    { value: "Y", label: "Yes" },
    { value: "N", label: "No" },
  ];

  const sanitizeYesNoField = (
    params: Record<string, any>,
    fieldName: string,
  ) => {
    const value = params[fieldName];
    if (value !== "Y" && value !== "N") {
      delete params[fieldName];
    }
  };

  const YES_NO_FIELDS = [
    "wasPickUpCompleted",
    "wasPickUpRescheduled",
    "isFreightInOsdBay",
    "didFreightMoveOnOd400",
    "isFreightLabeledForAnotherCustomer",
    "wasDeliveredToWrongAddress",
    "doesAs400AddressMatchBol",
    "doesBolShowSlc",
    "isFreightLabeledShowingMsdAddress",
    "isTrueSaiaError",
  ] as const;

  const sanitizeNumberField = (
    params: Record<string, any>,
    fieldName: string,
  ) => {
    const value = params[fieldName];

    if (value === "" || value === null || value === undefined) {
      delete params[fieldName];
      return;
    }

    if (typeof value === "number") {
      return;
    }

    const coerced = Number(value);
    if (Number.isFinite(coerced)) {
      params[fieldName] = coerced;
    } else {
      delete params[fieldName];
    }
  };

  const sanitizeInvestigationPayload = (rawValues: Record<string, any>) => {
    const params: Record<string, any> = { ...rawValues };

    const omit = (...fields: string[]) => {
      for (const field of fields) delete params[field];
    };

    // Radio fields: backend validates strictly as Y/N. On Save, we allow partial forms,
    // so omit any unset values ("", undefined) to avoid 400 validation errors.
    for (const fieldName of YES_NO_FIELDS) {
      sanitizeYesNoField(params, fieldName);
    }

    // Drop dependent fields when not applicable (prevents stale values in "Save" payload).
    if (params.wasPickUpCompleted !== "N") {
      omit("wasPickUpRescheduled", "pickupNumber", "reasonUnableToPickUp");
    } else if (params.wasPickUpRescheduled !== "Y") {
      omit("pickupNumber");
    }
    if (params.wasPickUpRescheduled !== "N") {
      omit("reasonUnableToPickUp");
    }

    if (params.isFreightInOsdBay !== "N") {
      omit("didFreightMoveOnOd400", "od400ProNumber");
    } else if (params.didFreightMoveOnOd400 !== "Y") {
      omit("od400ProNumber");
    }

    if (params.isFreightLabeledShowingMsdAddress !== "N") {
      omit(
        "misdeliveryAddress1",
        "misdeliveryAddress2",
        "misdeliveryCity",
        "misdeliveryState",
        "misdeliveryZip",
      );
    }

    // Numeric fields
    sanitizeNumberField(params, "od400ProNumber");
    sanitizeNumberField(params, "pickupNumber");

    return params;
  };

  const applyZeroForMissingExistingNumericFields = (
    params: Record<string, any>,
    investigation: IMisdeliveryInvestigation | null | undefined,
  ) => {
    if (
      params.didFreightMoveOnOd400 === "Y" &&
      investigation?.od400ProNumber &&
      !params.od400ProNumber
    ) {
      params.od400ProNumber = 0;
    }
    if (
      params.wasPickUpRescheduled === "Y" &&
      investigation?.pickupNumber &&
      !params.pickupNumber
    ) {
      params.pickupNumber = 0;
    }
  };

  // Helper: Focus field by name
  const focusFieldByName = useCallback((name: string) => {
    document?.getElementsByName(name)?.[0]?.focus();
  }, []);

  // Helper: Validate OD400 if applicable
  const validateOd400IfNeeded = useCallback(async (): Promise<boolean> => {
    if (!isOd400Applicable(formik.values)) return true;

    const od400Value = String(
      (formik.values as any).od400ProNumber ?? "",
    ).trim();
    if (!od400Value) return true;

    const isValid = await validateOd400ProNumber(od400Value);
    if (!isValid) {
      focusFieldByName("od400ProNumber");
    }
    return isValid;
  }, [formik.values, focusFieldByName, validateOd400ProNumber]);

  // Helper: Validate all fields for complete action
  const validateAllFields = useCallback(async (): Promise<boolean> => {
    const errors = await formik.validateForm();
    formik.setErrors(errors);

    if (Object.keys(errors).length > 0) {
      focusFieldByName(Object.keys(errors)?.[0]);
      touchAllFields();
      return false;
    }
    return true;
  }, [formik, focusFieldByName, touchAllFields]);

  const saveOrComplete = async (action: "save" | "complete" = "save") => {
    const isComplete = action === "complete";

    if (isComplete) {
      hasAttemptedCompleteRef.current = true;
    }

    // Validate OD400 when applicable
    const isOd400Valid = await validateOd400IfNeeded();
    if (!isOd400Valid) return;

    // Validate all fields if completing
    if (isComplete) {
      const allFieldsValid = await validateAllFields();
      if (!allFieldsValid) return;
    }

    const params = sanitizeInvestigationPayload(formik.values as any);
    setLoading(true);
    applyZeroForMissingExistingNumericFields(params, investigation);

    try {
      await SaveOrCompleteMSDInvestigation(params, osdNumber, action);
      const message =
        action === EFormAction.complete
          ? ErrorMessages.ohInvestigationNoteCompleted
          : ErrorMessages.investigationNoteSaved;

      dispatch(
        PopoutNotification(
          NOTIFICATION_TYPE.SUCCESS,
          message,
          Date.now().toString(),
        ),
      );

      setIsEditing(false);
      lastCreatedValuesRef.current = { ...(formik.values as any) };
      dispatch(isFormValueChanged(false));
      hasAttemptedCompleteRef.current = false;
      if (onRefresh) {
        await onRefresh();
      }
    } catch (error) {
      dispatch(
        PopoutNotification(
          NOTIFICATION_TYPE.ERROR,
          typeof error === "string"
            ? error
            : `OS&D investigation notes ${action} failed`,
          Date.now().toString(),
        ),
      );
      console.error("API call failed:", error);
    } finally {
      setLoading(false);
    }
  };

  // ─── Style Constants ────────────────────────────────────────────────────────
  const buttonStyles = {
    clearCancel: { marginRight: "8px", color: "white", borderRadius: "12px" },
    save: { marginRight: "8px", backgroundColor: "#2196f3", color: "white" },
    complete: {
      marginRight: "8px",
      backgroundColor: "#4caf50",
      color: "white",
    },
    overageEntry: { backgroundColor: "#ff9800", color: "white" },
  };

  // ─── Action Buttons Renderer ─────────────────────────────────────────────────
  const renderActionButtons = () => {
    // Investigation complete → only Overage Entry
    if (investigationStatus === MisDeliveryStatus.investigationComplete) {
      return (
        <Button
          type="button"
          variant="contained"
          className="entry"
          onClick={handleOverageEntryClick}
          style={buttonStyles.overageEntry}
        >
          Overage Entry
        </Button>
      );
    }

    // Editing mode → Clear/Cancel + Save + optional Complete
    if (isEditing) {
      const clearOrCancelLabel = bolGenerated ? "Clear" : "Cancel";

      return (
        <>
          <Button
            type="button"
            variant="outlined"
            className="clear-btn"
            onClick={() => {
              formik.resetForm();
              dispatch(isFormValueChanged(false));
              hasAttemptedCompleteRef.current = false;
            }}
            style={buttonStyles.clearCancel}
          >
            {clearOrCancelLabel}
          </Button>

          <Button
            type="button"
            variant="contained"
            className="entry"
            onClick={() => saveOrComplete()}
            style={buttonStyles.save}
          >
            Save
          </Button>

          {canViewCompleteButton && (
            <Button
              type="button"
              variant="contained"
              className="entry"
              onClick={() => saveOrComplete("complete")}
              style={buttonStyles.complete}
            >
              Complete
            </Button>
          )}
        </>
      );
    }

    // Default → Edit button
    return (
      <Button
        type="button"
        variant="contained"
        className="assign mr-0 ml"
        onClick={() => {
          lastCreatedValuesRef.current = { ...formik.values };
          dispatch(isFormValueChanged(false));
          setIsEditing(true);
        }}
      >
        Edit
      </Button>
    );
  };

  useEffect(() => {
    if (!isEditing) {
      dispatch(isFormValueChanged(false));
      return;
    }

    const lastCreatedValues = lastCreatedValuesRef.current;
    if (!lastCreatedValues) return;

    const formData = formik.values as any;
    const hasChanged = !compareObjects(formData, lastCreatedValues as any);
    dispatch(isFormValueChanged(hasChanged));
  }, [dispatch, formik.values, isEditing]);

  useEffect(() => {
    if (isExpanded) {
      setIsEditing(bolGenerated);
      formik.resetForm();
      lastCreatedValuesRef.current = { ...(formik.initialValues as any) };
      dispatch(isFormValueChanged(false));
      hasAttemptedCompleteRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpanded]);

  useEffect(() => {
    // Whenever the investigation data changes (refetch / response update),
    // keep the form in view mode and sync current values to the new initial values.
    formik.resetForm();
    lastCreatedValuesRef.current = { ...(formik.initialValues as any) };
    dispatch(isFormValueChanged(false));
    hasAttemptedCompleteRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [investigation]);

  useEffect(() => {
    setIsEditing(bolGenerated);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [investigationStatus]);

  return (
    <>
      <Box className="misdelivery-ivestigation-container">
        <form onSubmit={formik.handleSubmit} noValidate>
          <Grid
            container
            spacing={2}
            className="filter-margin"
            id="investigation-form"
          >
            <FormikRadioGroupField
              formik={formik}
              name="wasPickUpCompleted"
              label="Was the misdelivered freight pick up completed ?"
              required
              options={YES_NO_OPTIONS}
              disabled={!isEditing}
              gridProps={S_GRID_PROPS}
            />

            {values.wasPickUpCompleted === "N" && (
              <FormikRadioGroupField
                formik={formik}
                name="wasPickUpRescheduled"
                label="Was pick up rescheduled ?"
                required
                options={YES_NO_OPTIONS}
                disabled={!isEditing}
                gridProps={S_GRID_PROPS}
              />
            )}

            {values.wasPickUpRescheduled === "Y" && (
              <FormikLabeledTextField
                formik={formik}
                name="pickupNumber"
                label="PU #"
                placeholder="Enter PU Number"
                required
                maxLength={7}
                type="number"
                disabled={!isEditing}
                gridProps={{ md: 3.2, xs: 12 }}
              />
            )}
            {values.wasPickUpRescheduled === "N" && (
              <FormikLabeledTextField
                formik={formik}
                name="reasonUnableToPickUp"
                label="Give reason why we are unable to pick up shipment?"
                placeholder="Enter reason"
                required
                maxLength={50}
                disabled={!isEditing}
                gridProps={{ md: 3.2, xs: 12 }}
              />
            )}
          </Grid>

          <Grid container spacing={2} className="filter-margin">
            <FormikRadioGroupField
              formik={formik}
              name="isFreightInOsdBay"
              label="Is the Misdelivered freight in the OS&D Bay ?"
              required
              options={YES_NO_OPTIONS}
              disabled={!isEditing}
              gridProps={S_GRID_PROPS}
            />

            {values.isFreightInOsdBay === "N" && (
              <FormikRadioGroupField
                formik={formik}
                name="didFreightMoveOnOd400"
                label="Did the freight move on an OD400"
                required
                options={YES_NO_OPTIONS}
                disabled={!isEditing}
                gridProps={S_GRID_PROPS}
              />
            )}
            {values.isFreightInOsdBay === "N" &&
              values.didFreightMoveOnOd400 === "Y" && (
                <LabeledTextField
                  id="od400ProNumber"
                  label="OD400 #"
                  placeholder="Enter OD400 Number"
                  required
                  type="number"
                  maxLength={11}
                  disabled={!isEditing}
                  value={(formik.values as any)?.od400ProNumber ?? ""}
                  onChange={formik.handleChange}
                  onBlur={async (e) => {
                    formik.handleBlur(e as any);

                    const nextValue = String(
                      e.currentTarget.value ?? "",
                    ).trim();
                    if (isOd400Applicable(formik.values) && !nextValue) {
                      if (hasAttemptedCompleteRef.current) {
                        formik.setFieldTouched("od400ProNumber", true, false);
                        formik.setFieldError("od400ProNumber", REQUIRED_MSG);
                      }
                      return;
                    }

                    await validateOd400ProNumber(nextValue);
                  }}
                  tooltip={{
                    text: "MSD should not move on OD400, if you cannot find pro the freight applies to have this entered as an overage.",
                    key: "od400ProNumber",
                  }}
                  error={
                    (formik.touched as any)?.od400ProNumber &&
                    typeof (formik.errors as any)?.od400ProNumber === "string"
                  }
                  helperText={
                    (formik.touched as any)?.od400ProNumber
                      ? ((formik.errors as any)?.od400ProNumber as
                          | string
                          | undefined)
                      : undefined
                  }
                  gridProps={S_GRID_PROPS}
                />
              )}
          </Grid>

          {/* Third row with labeled for another customer control */}
          <Grid container spacing={2} className="filter-margin">
            <FormikRadioGroupField
              formik={formik}
              name="isFreightLabeledForAnotherCustomer"
              label="Is the Misdelivered freight labeled for another customer ?"
              required
              options={YES_NO_OPTIONS}
              disabled={!isEditing}
              gridProps={M_GRID_PROPS}
            />

            <FormikRadioGroupField
              formik={formik}
              name="wasDeliveredToWrongAddress"
              label="Was this delivered to the wrong address ?"
              required
              options={YES_NO_OPTIONS}
              disabled={!isEditing}
              gridProps={M_GRID_PROPS}
            />
          </Grid>

          {/* Fourth row with AS400 BOL address match control */}
          <Grid container spacing={2} className="filter-margin">
            <Grid item {...XL_GRID_PROPS}>
              <FormikRadioGroupField
                formik={formik}
                name="doesAs400AddressMatchBol"
                label="Does the address in the AS400 match the address in the BOL ?"
                required
                options={YES_NO_OPTIONS}
                disabled={!isEditing}
                gridProps={XL_GRID_PROPS}
              />

              {values.doesAs400AddressMatchBol === "N" && (
                <Grid container spacing={2} className="filter-margin">
                  <Grid item {...M_GRID_PROPS} className="mt-10" data-testid="billing-error-note">
                    <NoteBox message="Note: This is billing error. Please submit a bill correction." />
                  </Grid>
                </Grid>
              )}
            </Grid>
          </Grid>

          {/* Fifth row with BOL SLC control */}
          <Grid container spacing={2} className="filter-margin">
            <FormikRadioGroupField
              formik={formik}
              name="doesBolShowSlc"
              label="Does the BOL for the misdelivered on PRO show SLC ?"
              required
              options={YES_NO_OPTIONS}
              disabled={!isEditing}
              gridProps={S_GRID_PROPS}
            />

            {/* New row with MSD address control */}

            {/* New field: Is the freight labeled? */}
            <FormikRadioGroupField
              formik={formik}
              name="isFreightLabeled"
              label="Is there a label on the freight?"
              required
              options={YES_NO_OPTIONS}
              disabled={!isEditing}
              gridProps={S_GRID_PROPS}
            />

            {/* Show the 'showing MSD address' question only when freight is labeled */}
            {values.isFreightLabeled === "Y" && (
              <FormikRadioGroupField
                formik={formik}
                name="isFreightLabeledShowingMsdAddress"
                label="Is this freight labeled showing the MSD address ?"
                required
                options={YES_NO_OPTIONS}
                disabled={!isEditing}
                gridProps={S_GRID_PROPS}
              />
            )}

            {values.isFreightLabeledShowingMsdAddress === "Y" && (
              <>
              <Grid item {...M_GRID_PROPS} className="mt-10"/>
              <Grid item {...M_GRID_PROPS} className="mt-10">
                <NoteBox data-testid="shipper-error-note" message="Note: This is a shipper error and a new BOL is required." />
              </Grid>
              </>
            )}

            {values.isFreightLabeledShowingMsdAddress === "N" && (
              <>
                <FormikLabeledTextField
                  formik={formik}
                  name="misdeliveryAddress1"
                  label="Address 1"
                  required={true}
                  placeholder="Enter address"
                  maxLength={30}
                  disabled={!isEditing}
                  gridProps={S_GRID_PROPS}
                />
                
                <FormikLabeledTextField
                  formik={formik}
                  name="misdeliveryAddress2"
                  label="Address 2"
                  placeholder="Enter address"
                  maxLength={30}
                  disabled={!isEditing}
                  gridProps={S_GRID_PROPS}
                />

                <Location
                  osdDetails={investigation}
                  formFieldsDisabled={!isEditing}
                  formik={formik}
                  key={formik.values.misdeliveryState}
                  formObject={{
                    cityField: "misdeliveryCity",
                    zipField: "misdeliveryZip",
                    stateField: "misdeliveryState",
                    cityLabel: "City",
                    zipLabel: "Zip",
                    stateLabel: "State",
                  }}
                  required={true}
                />
              </>
            )}
          </Grid>

          <h3 className="accordion-content-header">Does the Label have a</h3>

          <Grid container spacing={2} className="filter-margin">
            <FormikLabeledTextField
              formik={formik}
              name="bolNumber"
              label="BOL #"
              placeholder="Enter BOL #"
              maxLength={20}
              disabled={!isEditing}
              gridProps={S_GRID_PROPS}
            />

            <FormikLabeledTextField
              formik={formik}
              name="shipperNumber"
              label="S/N #"
              placeholder="Enter S/N #"
              maxLength={20}
              disabled={!isEditing}
              gridProps={S_GRID_PROPS}
            />

            <FormikLabeledTextField
              formik={formik}
              name="poNumber"
              label="PO #"
              placeholder="Enter PO #"
              maxLength={20}
              disabled={!isEditing}
              gridProps={S_GRID_PROPS}
            />
          <FormikRadioGroupField
            formik={formik}
            name="isTrueSaiaError"
            label="Is this is a true SAIA error ?"
            required
            options={YES_NO_OPTIONS}
            disabled={!isEditing}
            gridProps={S_GRID_PROPS}
            tooltipText={
              formik.values.isTrueSaiaError === "N"
                ? "Email at OSDCLP@saia.com."
                : undefined
            }
            tooltipKey={
              formik.values.isTrueSaiaError === "N"
                ? "isTrueSaiaError"
                : undefined
            }
          />
          </Grid>

          {/* New row with driver name text input */}
          <Grid container spacing={2} className="filter-margin">
            <FormikLabeledTextField
              formik={formik}
              name="driverName"
              label="Name of driver who delivered the misdelivered shipment ?"
              placeholder="Enter driver name"
              required
              maxLength={30}
              disabled={!isEditing}
              gridProps={M_GRID_PROPS}
            />

            <FormikLabeledTextField
              formik={formik}
              name="dockWorkerName"
              label="Name of dockworker who loaded misdelivered shipment on to PUD TRL ?"
              placeholder="Enter dockworker name"
              required
              maxLength={30}
              disabled={!isEditing}
              gridProps={M_GRID_PROPS}
            />
          </Grid>

          <Grid
            container
            spacing={2}
            className="filter-margin"
            justifyContent="flex-end"
          >
            {investigationStatus !== MisDeliveryStatus.hazmatPending && !isCleared && (
              <Grid item>{renderActionButtons()}</Grid>
            )}
          </Grid>
        </form>
      </Box>
      <Loader pageLoader={loading}></Loader>
    </>
  );
};

export default MisdeliveryInvestigation;
